import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, BookOpen, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { pageHead, jsonLd, SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/clubs")({
  head: () => pageHead({
    path: "/clubs",
    title: "Neighborhood reading clubs — Book Swap",
    description: "Join or start a reading club with neighbors: pick a shared book, meet locally and swap copies through Book Swap.",
    scripts: [
      jsonLd({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Neighborhood reading clubs",
        description: "Local reading clubs where neighbors pick a shared book, meet nearby and swap copies.",
        url: `${SITE_URL}/clubs`,
        about: { "@type": "Thing", name: "Neighborhood reading clubs" },
      }),
    ],
  }),
  component: Clubs,
});

type Club = {
  id: string;
  name: string;
  description: string | null;
  cover_url: string | null;
  current_book_id: string | null;
  host_user_id: string;
  member_count?: number;
  joined?: boolean;
};

function Clubs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);

  async function load() {
    const { data } = await supabase.from("reading_clubs").select("*").order("created_at", { ascending: false });
    const list = (data ?? []) as Club[];
    if (list.length) {
      const { data: members } = await supabase
        .from("reading_club_members")
        .select("club_id,user_id")
        .in("club_id", list.map((c) => c.id));
      list.forEach((c) => {
        const ms = (members ?? []).filter((m: any) => m.club_id === c.id);
        c.member_count = ms.length;
        c.joined = !!user && ms.some((m: any) => m.user_id === user.id);
      });
    }
    setClubs(list);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);

  async function create() {
    if (!user) { navigate({ to: "/login" }); return; }
    if (!name.trim()) return toast.error("Give your club a name.");
    setCreating(true);
    const { data, error } = await supabase
      .from("reading_clubs")
      .insert({ name: name.trim(), description: desc.trim() || null, host_user_id: user.id })
      .select().single();
    if (!error && data) {
      await supabase.from("reading_club_members").insert({ club_id: (data as any).id, user_id: user.id });
      toast.success("Club created");
      setName(""); setDesc("");
      load();
    } else if (error) {
      toast.error("Could not create club.");
    }
    setCreating(false);
  }

  async function toggleJoin(c: Club) {
    if (!user) { navigate({ to: "/login" }); return; }
    if (c.joined) {
      await supabase.from("reading_club_members").delete().eq("club_id", c.id).eq("user_id", user.id);
    } else {
      await supabase.from("reading_club_members").insert({ club_id: c.id, user_id: user.id });
    }
    load();
  }

  async function deleteClub(c: Club) {
    if (!user || c.host_user_id !== user.id) return;
    if (!confirm(`Delete "${c.name}"? Its members will be removed too.`)) return;
    const { error } = await supabase.from("reading_clubs").delete().eq("id", c.id);
    if (error) { console.error(error); return toast.error("Could not delete this club."); }
    toast.success("Club deleted");
    load();
  }


  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="rounded-3xl p-8 text-white shadow-[var(--shadow-elegant)]" style={{ background: "var(--gradient-hero)" }}>
          <Users className="h-8 w-8 opacity-80" />
          <h1 className="mt-3 font-serif text-3xl text-white md:text-4xl">Reading clubs</h1>
          <p className="mt-2 max-w-xl text-white/80">Read the same book together — share thoughts, meet up, swap when done.</p>
        </div>

        {user && (
          <div className="mt-8 rounded-2xl bg-card p-5 shadow-[var(--shadow-card)]">
            <h2 className="font-serif text-lg font-semibold">Start a new club</h2>
            <div className="mt-3 grid gap-3">
              <Input placeholder="Club name (e.g. Sunset Sci-Fi)" value={name} onChange={(e) => setName(e.target.value)} />
              <Textarea placeholder="What's this club about?" value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={400} />
              <Button onClick={create} disabled={creating} className="w-fit bg-primary hover:bg-primary/90">
                <Plus className="mr-2 h-4 w-4" /> {creating ? "Creating…" : "Create club"}
              </Button>
            </div>
          </div>
        )}

        <section className="mt-8">
          <h2 className="mb-4 font-serif text-2xl">All clubs</h2>
          {clubs.length === 0 ? (
            <p className="text-muted-foreground">No clubs yet — be the first to start one.</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {clubs.map((c) => (
                <div key={c.id} className="rounded-2xl bg-card p-5 shadow-[var(--shadow-card)] transition hover:shadow-[var(--shadow-elegant)]">
                  <div className="flex items-start gap-3">
                    <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary-soft text-primary">
                      <BookOpen className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif text-lg font-semibold">{c.name}</h3>
                      {c.description && <p className="mt-1 text-sm text-muted-foreground">{c.description}</p>}
                      <p className="mt-2 text-xs text-muted-foreground">{c.member_count ?? 0} members</p>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" variant={c.joined ? "outline" : "default"} onClick={() => toggleJoin(c)} className={c.joined ? "" : "bg-primary hover:bg-primary/90"}>
                      {c.joined ? "Leave" : "Join"}
                    </Button>
                    {c.host_user_id && (
                      <Link to="/u/$id" params={{ id: c.host_user_id }}>
                        <Button size="sm" variant="ghost">View host</Button>
                      </Link>
                    )}
                    {user?.id === c.host_user_id && (
                      <Button size="sm" variant="ghost" onClick={() => deleteClub(c)} className="text-destructive hover:bg-destructive/10">
                        <Trash2 className="me-1 h-4 w-4" /> Delete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
